/**
 * Standard T-Hangar Row Template Parameters
 *
 * Defines form parameters and Zod schema for T-hangar rows.
 * T-hangars have alternating T-shaped units that interlock into
 * a rectangular building envelope.
 */

import { z } from 'zod'
import type { BaseTemplateParams, DoorType, DoorSpec, UnitType } from '../types'

// ============================================================================
// ZOD SCHEMAS
// ============================================================================

/** Door spec schema for per-unit overrides */
const doorSpecSchema = z.object({
  offset_ft: z.number(),
  width_ft: z.number(),
  height_ft: z.number(),
  type: z.enum(['bifold', 'sliding', 'hydraulic', 'rolling']),
})

/** Doors config schema for per-unit overrides */
export const tHangarUnitDoorsConfigSchema = z.object({
  front: z.array(doorSpecSchema).optional(),
})

/** Unit override for T-hangar units */
export const tHangarUnitOverrideSchema = z.object({
  index: z.number().int().min(0),
  type: z.enum([
    'conventional_hangar', 'box_hangar', 't_hangar', 'shade',
    'tiedown', 'ramp', 'office', 'storage', 'shop', 'custom',
  ]).optional(),
  doors: tHangarUnitDoorsConfigSchema.optional(),
})

/** T-hangar parameters schema */
export const tHangarParamsSchema = z.object({
  // Base params
  name: z.string().min(1, 'Name is required'),
  code: z.string().min(1, 'Code is required').max(10, 'Code too long'),
  latitude: z.number(),
  longitude: z.number(),
  bearing_deg: z.number().transform(val => ((val % 360) + 360) % 360),
  floor_color: z.string(),
  wall_color: z.string(),

  // T-shape dimensions
  unit_width_ft: z.number()
    .min(20, 'Unit width must be at least 20 ft')
    .max(80, 'Unit width must be at most 80 ft'),
  wing_depth_ft: z.number()
    .min(10, 'Wing depth must be at least 10 ft')
    .max(60, 'Wing depth must be at most 60 ft'),
  tail_width_ft: z.number()
    .min(10, 'Tail width must be at least 10 ft')
    .max(60, 'Tail width must be at most 60 ft'),
  height_ft: z.number()
    .min(8, 'Height must be at least 8 ft')
    .max(30, 'Height must be at most 30 ft'),

  // Unit configuration
  unit_count: z.number().int()
    .min(2, 'At least 2 units required')
    .max(30, 'Max 30 units'),

  // Door and wall
  default_door_type: z.enum(['bifold', 'sliding', 'hydraulic', 'rolling']),
  default_door_width_ft: z.number()
    .min(10, 'Door width must be at least 10 ft')
    .max(80, 'Door width must be at most 80 ft'),
  default_door_height_ft: z.number()
    .min(8, 'Door height must be at least 8 ft')
    .max(30, 'Door height must be at most 30 ft'),
  wall_thickness_ft: z.number().min(0.25).max(2).default(0.5),

  // Per-unit overrides
  unit_overrides: z.array(tHangarUnitOverrideSchema).default([]),
}).refine(
  (data) => data.tail_width_ft < data.unit_width_ft,
  { message: 'Tail width must be less than unit width', path: ['tail_width_ft'] }
)

// ============================================================================
// TYPES
// ============================================================================

/** T-hangar template parameters interface */
export interface THangarParams extends BaseTemplateParams {
  unit_width_ft: number
  wing_depth_ft: number
  tail_width_ft: number
  height_ft: number
  unit_count: number
  default_door_type: DoorType
  default_door_width_ft: number
  default_door_height_ft: number
  wall_thickness_ft: number
  unit_overrides: Array<{
    index: number
    type?: UnitType
    doors?: { front?: DoorSpec[] }
  }>
}

// ============================================================================
// DEFAULT VALUES
// ============================================================================

/** Default values for standard T-hangar row template */
export const DEFAULT_T_HANGAR_VALUES: THangarParams = {
  name: 'New T-Hangar Row',
  code: 'TH',
  latitude: 0,
  longitude: 0,
  bearing_deg: 0,
  floor_color: '#808080',
  wall_color: '#A0A0A0',

  unit_width_ft: 44.5,
  wing_depth_ft: 21,
  tail_width_ft: 22.5,
  height_ft: 14,

  unit_count: 10,

  default_door_type: 'rolling',
  default_door_width_ft: 40,
  default_door_height_ft: 12,
  wall_thickness_ft: 0.5,

  unit_overrides: [],
}

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Calculate step width — the notch on each side of the T-shape
 * step_width = (unit_width - tail_width) / 2
 */
export function calculateStepWidth(params: THangarParams): number {
  return (params.unit_width_ft - params.tail_width_ft) / 2
}

/**
 * Calculate unit pitch — center-to-center distance along the row
 * unit_pitch = (unit_width + tail_width) / 2 + wall_thickness
 */
export function calculateUnitPitch(params: THangarParams): number {
  return (params.unit_width_ft + params.tail_width_ft) / 2 + params.wall_thickness_ft
}

/**
 * Calculate T-shape floor area for a single unit
 * T-shape = wing_bay + tail_bay
 */
export function calculateTShapeArea(params: THangarParams): number {
  const wingArea = params.unit_width_ft * params.wing_depth_ft
  const tailArea = params.tail_width_ft * params.wing_depth_ft // tail_depth = wing_depth for perfect interlock
  return wingArea + tailArea
}

/**
 * Create a default T-hangar door spec from params.
 * Door is centered on the front edge, clamped to unit_width - 4 (2ft frame each side).
 */
export function createDefaultTHangarDoorSpec(params: THangarParams): DoorSpec {
  const doorWidth = Math.min(params.default_door_width_ft, params.unit_width_ft - 4)
  return {
    offset_ft: (params.unit_width_ft - doorWidth) / 2,
    width_ft: doorWidth,
    height_ft: params.default_door_height_ft,
    type: params.default_door_type,
  }
}
